import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import clsx from 'clsx';
import { Alert, Form, Input, Select, Divider, Spin, Switch, Tooltip, } from 'antd';
import { LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { fragileTxt, txt } from 'libs/i18n';
import { getProfilesForLanguage, getSingleProfileForLanguage, selectInitialLanguage, } from 'libs/locales/language-utils';
import { createProjectForStreaming } from 'libs/media-processing/media-upload';
import { fetchTranscriptionProfiles, validateTranscriptionProfiles, extractLanguagesFromProfiles, getPurposeFromSource, } from 'api/user-api';
import { transcribeLink } from 'api/model/services/services-api';
import { getProjectLanguage, setupNewProject } from 'api/project-api';
import { useSession } from 'components/Authenticated';
import ModalFooter from 'components/ModalFooter';
import LanguageSelect from 'components/LanguageSelect';
import { useDashboard } from 'screens/DashboardScreen/dashboard-context';
import { deviceSignal, setTranscriptionSourceSignal } from 'screens/DashboardScreen/pages/EditorPage/signalsStore/transcriptionSource';
import { MediaOriginsList } from '../../../media-upload/media-origins-list';
import AdvancedSettings from './AdvancedSettings';
import MediaSection from './MediaSection';
import { loadLastSelectedProfilesFromStorage, saveLastSelectedProfilesToStorage, } from './last-selected-profile-storage';
import { findLanguageWithProfiles } from './find-profiles';
import styles from './style.module.less';
const emptyOriginsList = new MediaOriginsList();
const FIREFLY_FILE_PROFILE = /(?:soniox|firefly)-files/;
const FIREFLY_DURATION_LIMIT_MINUTES = 300;
const LoadedContent = ({ onClose, onSubmit, initialLocale, credit, project, remainingStorage, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    const { session, updateUserSettings } = useSession();
    const { connection, login: { user: { settings: { transcriptionSettings } } } } = session;
    const { uploadQueueController } = useDashboard();
    useSignals();
    const [isLoadingUrls, setIsLoadingUrls] = useState(false);
    const [selectedSource, setSelectedSource] = useState('file');
    const [profilesState, setProfilesState] = useState({ status: 'loading' });
    const [lastSelectedProfiles, setLastSelectedProfiles] = useState(() => loadLastSelectedProfilesFromStorage());
    const initialValues = {
        originsList: emptyOriginsList,
        projectName: (_a = project === null || project === void 0 ? void 0 : project.description.name) !== null && _a !== void 0 ? _a : null,
        language: initialLocale,
        transcriptionProfile: (_c = (_b = project === null || project === void 0 ? void 0 : project.transcriptionConfig) === null || _b === void 0 ? void 0 : _b.profile) !== null && _c !== void 0 ? _c : transcriptionSettings.languageModel,
        trsxFile: null,
        usePPC: (_e = (_d = project === null || project === void 0 ? void 0 : project.transcriptionConfig) === null || _d === void 0 ? void 0 : _d.withPPC) !== null && _e !== void 0 ? _e : transcriptionSettings.usePpc,
        useVAD: (_g = (_f = project === null || project === void 0 ? void 0 : project.transcriptionConfig) === null || _f === void 0 ? void 0 : _f.withVAD) !== null && _g !== void 0 ? _g : transcriptionSettings.useVad,
        useDiarization: (_j = (_h = project === null || project === void 0 ? void 0 : project.transcriptionConfig) === null || _h === void 0 ? void 0 : _h.withDiarization) !== null && _j !== void 0 ? _j : transcriptionSettings.useDiarization,
        useUserLex: (_l = (_k = project === null || project === void 0 ? void 0 : project.transcriptionConfig) === null || _k === void 0 ? void 0 : _k.withUserLex) !== null && _l !== void 0 ? _l : transcriptionSettings.useUserLex,
        micId: 'default',
    };
    const [form] = Form.useForm();
    // FIXME: @podlomar - I am not sure if this is even fixable.
    // At first render, useWatch returns undefined but after thet the form values are
    // always set to proper values so there is no point in allowing them
    // to be undefined in TranscribeFormValues. Maybe this is a bug in the antd form library,
    // maybe there is a way do this properly somehow.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const originsList = (_m = Form.useWatch('originsList', form)) !== null && _m !== void 0 ? _m : initialValues.originsList;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const language = (_o = Form.useWatch('language', form)) !== null && _o !== void 0 ? _o : initialValues.language;
    const transcriptionProfile = (_p = Form.useWatch('transcriptionProfile', form)) !== null && _p !== void 0 ? _p : initialValues.transcriptionProfile;
    const isOpenTeam = session.login.hasClaim('teamProjects:enabled');
    const transcribeExistingProject = project !== undefined && session.login.hasClaim('projects:accessFailedTranscriptions');
    const allOrigins = originsList.getAllOrigins();
    const isFireflyProfile = FIREFLY_FILE_PROFILE.test(transcriptionProfile !== null && transcriptionProfile !== void 0 ? transcriptionProfile : '');
    const exceedsFireflyDurationLimit = isFireflyProfile
        && selectedSource !== 'mic'
        && originsList.containsMediaLongerOrEqualTo(FIREFLY_DURATION_LIMIT_MINUTES);
    const profiles = useMemo(() => {
        return profilesState.status === 'success' ? profilesState.profiles : [];
    }, [profilesState]);
    const isLiveProfile = selectedSource === 'mic' && profiles.length > 0;
    const getAvailableLanguages = () => {
        return profilesState.status === 'success'
            ? extractLanguagesFromProfiles(profilesState.profiles)
            : [];
    };
    const isLoadingProfiles = () => {
        return profilesState.status === 'loading';
    };
    const getLastSelectedProfile = (lang, purpose) => {
        var _a;
        const key = `${lang}_${purpose !== null && purpose !== void 0 ? purpose : ''}`;
        return (_a = lastSelectedProfiles.get(key)) !== null && _a !== void 0 ? _a : null;
    };
    const saveLastSelectedProfile = (lang, purpose, profile) => {
        const key = `${lang}_${purpose !== null && purpose !== void 0 ? purpose : ''}`;
        setLastSelectedProfiles((prev) => {
            const newMap = new Map(prev).set(key, profile);
            saveLastSelectedProfilesToStorage(newMap);
            return newMap;
        });
    };
    const handleProfileChange = (lang, purpose, profile) => {
        form.setFieldValue('transcriptionProfile', profile);
        saveLastSelectedProfile(lang, purpose, profile);
    };
    const selectTranscriptionProfile = (profilesByPurpose, selectedLanguage, profilesForLanguage, purpose) => {
        const singleProfile = getSingleProfileForLanguage(profilesByPurpose, selectedLanguage);
        if (singleProfile !== null) {
            handleProfileChange(selectedLanguage.code, purpose, singleProfile.profile);
            return;
        }
        if (profilesForLanguage.length > 1) {
            const lastSelected = getLastSelectedProfile(selectedLanguage.code, purpose);
            // NOTE: Backend returns profiles in priority order
            const profileToUse = lastSelected !== null && profilesForLanguage.some((p) => p.profile === lastSelected)
                ? lastSelected
                : profilesForLanguage[0].profile;
            handleProfileChange(selectedLanguage.code, purpose, profileToUse);
            return;
        }
        handleProfileChange(selectedLanguage.code, purpose, profilesForLanguage[0].profile);
    };
    const loadProfilesByPurpose = async (purpose, lang) => {
        var _a;
        setProfilesState({ status: 'loading' });
        const result = await fetchTranscriptionProfiles(connection, purpose);
        if (result.isFail()) {
            setProfilesState({
                status: 'error',
                error: txt('noTranscriptionProfiles'),
            });
            return;
        }
        const profilesByPurpose = result.get();
        if (profilesByPurpose.length === 0) {
            setProfilesState({ status: 'error', error: txt('noTranscriptionProfiles') });
            return;
        }
        const availableLanguages = extractLanguagesFromProfiles(profilesByPurpose);
        if (availableLanguages.length === 0) {
            setProfilesState({ status: 'error', error: txt('noTranscriptionProfiles') });
            return;
        }
        setProfilesState({ status: 'success', profiles: profilesByPurpose });
        const currentLanguage = (_a = lang !== null && lang !== void 0 ? lang : form.getFieldValue('language')) !== null && _a !== void 0 ? _a : selectInitialLanguage(availableLanguages, initialLocale);
        const languageWithProfiles = findLanguageWithProfiles(initialLocale, profilesByPurpose, currentLanguage, availableLanguages);
        if (languageWithProfiles === null) {
            setProfilesState({ status: 'error', error: txt('noTranscriptionProfiles') });
            return;
        }
        form.setFieldValue('language', languageWithProfiles.language);
        selectTranscriptionProfile(profilesByPurpose, languageWithProfiles.language, languageWithProfiles.profiles, purpose);
    };
    const startLinkTranscription = async (projectToUpdate, mediaUrl, transcribeAttrs) => {
        var _a;
        const projectName = (_a = form.getFieldValue('projectName')) !== null && _a !== void 0 ? _a : mediaUrl.getName();
        const newProject = projectToUpdate === 'new' ? await setupNewProject(connection, projectName) : projectToUpdate;
        await transcribeLink(connection, mediaUrl.url, newProject.description.name, newProject.id, transcribeAttrs);
        return newProject;
    };
    useEffect(() => {
        const purpose = getPurposeFromSource(selectedSource);
        void loadProfilesByPurpose(purpose, language);
    }, [selectedSource, language]);
    const fileDiarizationDefault = (_r = (_q = project === null || project === void 0 ? void 0 : project.transcriptionConfig) === null || _q === void 0 ? void 0 : _q.withDiarization) !== null && _r !== void 0 ? _r : transcriptionSettings.useDiarization;
    const filePpcDefault = (_t = (_s = project === null || project === void 0 ? void 0 : project.transcriptionConfig) === null || _s === void 0 ? void 0 : _s.withPPC) !== null && _t !== void 0 ? _t : transcriptionSettings.usePpc;
    useEffect(() => {
        const updateProjectName = async () => {
            if (originsList.areOriginsResolved()) {
                const projectName = allOrigins.length === 1
                    ? allOrigins[0].getName()
                    : null;
                form.setFieldsValue({ projectName });
                return;
            }
            const resolvedOriginsList = await originsList.resolveOrigins(connection, (partialList) => form.setFieldsValue({ originsList: partialList }));
            form.setFieldsValue({ originsList: resolvedOriginsList });
        };
        void updateProjectName();
    }, [originsList]);
    // Note: Necessary to load project data into form as initial values.
    useEffect(() => {
        const setInitialValues = async () => {
            if (project !== undefined) {
                const projectLanguage = await getProjectLanguage(session.connection, project.id);
                if (projectLanguage !== null) {
                    form.setFieldsValue(Object.assign(Object.assign({}, initialValues), { language: projectLanguage }));
                }
                form.setFieldsValue(Object.assign({}, initialValues));
            }
            else {
                form.setFieldsValue(initialValues);
            }
        };
        void setInitialValues();
    }, []);
    useEffect(() => {
        if (profiles.length > 0) {
            const profilesForLanguage = getProfilesForLanguage(profiles, language);
            const purpose = getPurposeFromSource(selectedSource);
            const currentProfile = form.getFieldValue('transcriptionProfile');
            const isCurrentProfileValid = currentProfile !== undefined
                && profilesForLanguage.some((p) => p.profile === currentProfile);
            if (!isCurrentProfileValid) {
                const suggestedProfile = transcriptionSettings.languageModel;
                const lastSelected = getLastSelectedProfile(language.code, purpose);
                const initialProfile = profilesForLanguage.some((profile) => profile.profile === suggestedProfile)
                    ? suggestedProfile
                    : lastSelected !== null && profilesForLanguage.some((profile) => profile.profile === lastSelected)
                        ? lastSelected
                        : profilesForLanguage.length > 0
                            ? profilesForLanguage[0].profile
                            : 'default';
                handleProfileChange(language.code, purpose, initialProfile);
            }
        }
    }, [language, selectedSource, profiles]);
    const getLanguageProfile = ((formValues) => {
        if (formValues.transcriptionProfile !== undefined) {
            return formValues.transcriptionProfile;
        }
        if (profiles.length > 0) {
            const singleProfile = getSingleProfileForLanguage(profiles, language);
            if (singleProfile !== null) {
                return singleProfile.profile;
            }
            const profilesForLanguage = getProfilesForLanguage(profiles, language);
            if (profilesForLanguage.length > 0) {
                return profilesForLanguage[0].profile;
            }
        }
        return 'default';
    });
    const handleSubmit = async (formValues) => {
        var _a;
        const mediaOriginsList = formValues.originsList;
        const isMic = selectedSource === 'mic';
        if (isMic && credit <= 0) {
            return;
        }
        if (!isMic && !mediaOriginsList.canSubmit(credit, remainingStorage, session.login.hasClaim('unlimitedStorage:enabled'))) {
            return;
        }
        if (exceedsFireflyDurationLimit) {
            return;
        }
        setTranscriptionSourceSignal(isMic ? 'mic' : 'file');
        if (profiles.length > 0) {
            const languageValidationsResponse = await validateTranscriptionProfiles(connection);
            const purpose = getPurposeFromSource(selectedSource);
            const languageValidation = languageValidationsResponse.languages.find((lv) => lv.lang === language.code);
            if (languageValidation !== undefined) {
                const count = purpose === 'file' ? languageValidation.fileCount : languageValidation.liveCount;
                if (count === 0) {
                    setProfilesState({ status: 'error', error: txt('noTranscriptionProfiles') });
                    return;
                }
            }
            else {
                setProfilesState({ status: 'error', error: txt('noTranscriptionProfiles') });
                return;
            }
        }
        const languageProfile = getLanguageProfile(formValues);
        const transcribeAttrs = Object.assign(Object.assign(Object.assign({}, initialValues), formValues), { profile: languageProfile, language: formValues.language, useVAD: formValues.useVAD, useSpeakerId: true, useUserLex: formValues.useUserLex });
        if (isMic) {
            const liveProfileName = languageProfile;
            const newProject = await createProjectForStreaming(connection, (_a = formValues.projectName) !== null && _a !== void 0 ? _a : txt('micRecordingName'), language.code, liveProfileName, formValues.useDiarization, formValues.usePPC);
            onSubmit({ type: 'mic', micProjectId: newProject.id });
        }
        else {
            uploadQueueController.enqueue(mediaOriginsList.files.map((mediaFile) => {
                var _a;
                return ({
                    uploadTarget: Object.assign(Object.assign({}, transcribeExistingProject
                        ? {
                            type: 'existing',
                            project,
                        }
                        : {
                            type: 'new',
                            projectName: (_a = formValues.projectName) !== null && _a !== void 0 ? _a : mediaFile.getName(),
                        }), { transcribeAttributes: transcribeAttrs }),
                    mediaFile,
                    isUploadOnly: transcribeAttrs.trsxFile !== null,
                });
            }));
        }
        setIsLoadingUrls(true);
        const urlProjects = await Promise.all(mediaOriginsList.urls.map((mediaUrl) => startLinkTranscription(transcribeExistingProject ? project : 'new', mediaUrl, transcribeAttrs)));
        setIsLoadingUrls(false);
        updateUserSettings({
            transcriptionSettings: Object.assign(Object.assign(Object.assign({}, transcriptionSettings), { useVad: formValues.useVAD, recognitionLocale: formValues.language.code, languageModel: languageProfile, useUserLex: formValues.useUserLex }), (isMic ? {} : {
                useDiarization: formValues.useDiarization,
                usePpc: formValues.usePPC,
            })),
        });
        form.resetFields();
        onClose();
        if (!isMic) {
            onSubmit({ type: 'media', mediaOriginsList, urlProjects });
        }
    };
    return (_jsxs(Form, { form: form, preserve: false, layout: "vertical", onFinish: handleSubmit, initialValues: initialValues, children: [_jsxs("div", { className: styles.modalBody, children: [_jsx(MediaSection, { selectedSource: selectedSource, setSelectedSource: setSelectedSource, originsList: originsList, emptyOriginsList: emptyOriginsList, credit: credit, remainingStorage: remainingStorage, transcribeExistingProject: transcribeExistingProject, exceedsFireflyDurationLimit: exceedsFireflyDurationLimit, fileDiarizationDefault: fileDiarizationDefault, filePpcDefault: filePpcDefault }), _jsx(Divider, { className: styles.divider }), isOpenTeam && (_jsx(Alert, { message: txt('openTeamSharing'), type: "warning", showIcon: true })), allOrigins.length < 2 && !transcribeExistingProject && (_jsx(Form.Item, { name: "projectName", className: styles.label, label: txt('projectName'), colon: false, required: false, rules: [
                            {
                                message: txt('nameRequired'),
                                required: true,
                                whitespace: true,
                            },
                        ], children: _jsx(Input, { placeholder: txt('untitledProj') }) })), _jsxs("div", { className: styles.languageSelect, children: [_jsx(Form.Item, { className: styles.label, name: "language", label: txt('langOfTranscription'), rules: [
                                    {
                                        required: true,
                                        message: txt('transcritpionLanguageRequired'),
                                    },
                                ], children: isLoadingProfiles() ? (_jsx(Select, { loading: true, placeholder: txt('loading') })) : (_jsx(LanguageSelect, { id: "select-transcription-language", locales: getAvailableLanguages() })) }), !isLiveProfile && selectedSource === 'mic' ? (_jsx(Tooltip, { title: txt('modelWarning'), children: _jsx(WarningOutlined, { style: { color: 'var(--ant-color-warning)', marginTop: '1.5rem' } }) })) : null] }), _jsx(Form.Item, { name: "useDiarization", label: txt('splitBySpeakers'), className: clsx(styles.radioGroup, styles.label), children: _jsx(Switch, { size: "small" }) }), _jsx(AdvancedSettings, { selectedSource: selectedSource, allOrigins: allOrigins, originsList: originsList, language: language, profilesState: profilesState, profiles: profiles, onProfileChange: handleProfileChange })] }), _jsx("div", { children: isLoadingUrls ? (_jsxs("div", { className: styles.loadingIndicator, children: [_jsx(Spin, { indicator: _jsx(LoadingOutlined, {}) }), _jsx("span", { children: txt('linkDownload') })] })) : (_jsx(ModalFooter, { id: "action-initiate-transcription", form: form, onCancel: onClose, disabled: (selectedSource === 'mic' && deviceSignal.value === undefined)
                        || exceedsFireflyDurationLimit, className: styles.footer, children: `${txt('transcribe')} ${(_u = fragileTxt(`in-${language.lang}`)) !== null && _u !== void 0 ? _u : ''}` })) })] }));
};
export default LoadedContent;
